import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DEFAULT_OBS_SETTINGS, type ObsSettings } from './useObs';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: ObsSettings;
  onSave: (settings: ObsSettings) => void;
};

export function SettingsDialog({
  open,
  onOpenChange,
  settings,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<ObsSettings>(settings);

  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  const update = <K extends keyof ObsSettings>(key: K, value: ObsSettings[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const numberField = (
    key: keyof ObsSettings,
    value: string,
    fallback: number,
  ) =>
    update(
      key,
      (value === '' ? fallback : Number(value)) as ObsSettings[typeof key],
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>OBS connection settings</DialogTitle>
          <DialogDescription>
            Connects from this browser straight to an OBS WebSocket server on
            the same network. Stored only in this browser&apos;s localStorage.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 grid gap-1.5">
              <Label htmlFor="obs-ip">Host IP</Label>
              <Input
                id="obs-ip"
                placeholder="10.0.96.59"
                value={draft.ip}
                onChange={(e) => update('ip', e.target.value.trim())}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="obs-port">Port</Label>
              <Input
                id="obs-port"
                placeholder="4455"
                value={draft.port}
                onChange={(e) => update('port', e.target.value.trim())}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="obs-password">Password</Label>
            <Input
              id="obs-password"
              type="password"
              placeholder="(empty if auth is off)"
              value={draft.password}
              onChange={(e) => update('password', e.target.value)}
            />
          </div>

          <div className="grid gap-2 rounded-lg border border-border p-3">
            <p className="font-medium text-sm">Preview quality</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-1.5">
                <Label
                  htmlFor="obs-fps"
                  className="text-muted-foreground text-xs"
                >
                  FPS
                </Label>
                <Input
                  id="obs-fps"
                  type="number"
                  min={0.5}
                  max={15}
                  step={0.5}
                  value={draft.previewFps}
                  onChange={(e) => numberField('previewFps', e.target.value, 4)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label
                  htmlFor="obs-width"
                  className="text-muted-foreground text-xs"
                >
                  Width (px)
                </Label>
                <Input
                  id="obs-width"
                  type="number"
                  min={160}
                  max={1920}
                  step={80}
                  value={draft.previewWidth}
                  onChange={(e) =>
                    numberField('previewWidth', e.target.value, 640)
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label
                  htmlFor="obs-quality"
                  className="text-muted-foreground text-xs"
                >
                  JPEG quality
                </Label>
                <Input
                  id="obs-quality"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={draft.previewQuality}
                  onChange={(e) =>
                    numberField('previewQuality', e.target.value, 50)
                  }
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Lower values = lighter network load. Preview is polled
              screenshots, not a live video feed.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              setDraft({
                ...DEFAULT_OBS_SETTINGS,
                ...{ ip: draft.ip, port: draft.port, password: draft.password },
              })
            }
          >
            Reset quality
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                onSave({
                  ...draft,
                  previewFps: clamp(draft.previewFps, 0.5, 15, 4),
                  previewWidth: clamp(draft.previewWidth, 160, 1920, 640),
                  previewQuality: clamp(draft.previewQuality, 1, 100, 50),
                });
                onOpenChange(false);
              }}
            >
              Save &amp; connect
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function clamp(value: number, min: number, max: number, fallback: number) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}
